import React, { useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { MOUSE_BUTTON_LEFT } from '~/engine/tool/MouseButton';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useUpdateMeasurementPoint } from '~/hooks/useUpdateMeasurementPoint';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    Measurement,
    MeasurementDraft,
    measurementDraftInFrame,
    measurementInFrame,
} from '~/domain/measurement/measurement';
import {
    highlightedMeasurementState,
    measurementDraftState,
    measurementListState,
} from '~/state/atoms/measurementAtoms';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import {
    angleDegAtVertex,
    euclideanDistanceMm,
    PointMm,
} from '~/domain/geometry/patientGeometry';
import {
    formatAngleDeg,
    formatDistanceCm,
    formatMeasurementValue,
} from '~/utils/measurementFormat';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    arePointsOnDisplayedSlice,
    canvasMidpoint,
    CanvasPoint,
    displayedSliceToleranceMm,
    imageToPaneFrame,
    patientToCanvasPoint,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import {
    AnnotationPointDrag,
    useAnnotationPointDrag,
    usePlaneRedrawTick,
} from '~/hooks/viewer/annotationOverlay';
import AnnotationOverlaySvg, {
    OFF_PLANE_OPACITY,
} from '../AnnotationOverlaySvg';

/** Accent colour for committed measurements (high contrast on CT grey). */
export const MEASUREMENT_COLOR = '#FFCA28';
/** Colour for the in-progress placement preview. */
const DRAFT_COLOR = '#4FC3F7';
/** Radius of the small dot used for an angle vertex or a lone first point. */
const DOT_RADIUS_PX = 2.5;
/** Half-length of a caliper end-cap tick (perpendicular to the line). */
const CAP_HALF_PX = 6;
const ANGLE_ARC_RADIUS_PX = 22;
/** Radius (and hit target) of a draggable edit handle, in px. */
const HANDLE_RADIUS_PX = 6;

interface MeasurementOverlayProps {
    view: PlaneView | null;
}

/** Unit vector from a to b in canvas space, or null if coincident. */
function unitTo(a: CanvasPoint, b: CanvasPoint): CanvasPoint | null {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = Math.hypot(dx, dy);
    if (length === 0) {
        return null;
    }
    return { x: dx / length, y: dy / length };
}

/**
 * SVG path for the interior angle arc at `vertex`, sweeping the short way
 * between the two arms. Returns null for a degenerate configuration.
 */
function angleArcPath(
    vertex: CanvasPoint,
    armA: CanvasPoint,
    armB: CanvasPoint
): string | null {
    const dirA = unitTo(vertex, armA);
    const dirB = unitTo(vertex, armB);
    if (!dirA || !dirB) {
        return null;
    }
    const a0 = Math.atan2(dirA.y, dirA.x);
    const a1 = Math.atan2(dirB.y, dirB.x);
    // Signed shortest delta in (-π, π], so the arc always spans the
    // interior (≤ 180°) angle.
    let delta = a1 - a0;
    while (delta <= -Math.PI) {
        delta += 2 * Math.PI;
    }
    while (delta > Math.PI) {
        delta -= 2 * Math.PI;
    }
    const start = {
        x: vertex.x + ANGLE_ARC_RADIUS_PX * Math.cos(a0),
        y: vertex.y + ANGLE_ARC_RADIUS_PX * Math.sin(a0),
    };
    const end = {
        x: vertex.x + ANGLE_ARC_RADIUS_PX * Math.cos(a0 + delta),
        y: vertex.y + ANGLE_ARC_RADIUS_PX * Math.sin(a0 + delta),
    };
    const sweepFlag = delta >= 0 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${ANGLE_ARC_RADIUS_PX} ${ANGLE_ARC_RADIUS_PX} 0 0 ${sweepFlag} ${end.x} ${end.y}`;
}

interface SegmentProps {
    a: CanvasPoint;
    b: CanvasPoint;
    color: string;
    dashed?: boolean;
}

const Segment = ({ a, b, color, dashed = false }: SegmentProps) => (
    <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={color}
        strokeWidth={1.75}
        strokeDasharray={dashed ? '5 4' : undefined}
    />
);

/** Small filled dot — used for an angle vertex or a lone first point. */
const Dot = ({ at, color }: { at: CanvasPoint; color: string }) => (
    <circle
        cx={at.x}
        cy={at.y}
        r={DOT_RADIUS_PX}
        fill={color}
        stroke="#000"
        strokeWidth={0.75}
    />
);

/**
 * A caliper-style end-cap: a short tick perpendicular to the measured line,
 * the way a ruler or caliper terminates. Reads as an instrument (and is
 * easier to land an endpoint on) rather than a generic dot. Falls back to a
 * {@link Dot} when the line direction is unknown (a lone first point).
 */
const Tick = ({
    at,
    dir,
    color,
}: {
    at: CanvasPoint;
    dir: CanvasPoint | null;
    color: string;
}) => {
    if (!dir) {
        return <Dot at={at} color={color} />;
    }
    // Perpendicular to the line direction (-dy, dx), scaled to the cap length.
    const px = -dir.y * CAP_HALF_PX;
    const py = dir.x * CAP_HALF_PX;
    return (
        <line
            x1={at.x - px}
            y1={at.y - py}
            x2={at.x + px}
            y2={at.y + py}
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
        />
    );
};

/**
 * The measured value, set on a low-opacity dark pill so it stays legible
 * over any grayscale (bright or dark anatomy). The pill width is estimated
 * from the text length — measurement labels are short, fixed-format numbers,
 * so an estimate is sufficient.
 */
const ValueLabel = ({
    at,
    text,
    color,
}: {
    at: CanvasPoint;
    text: string;
    color: string;
}) => {
    const padX = 5;
    const height = 18;
    const width = text.length * 7.5 + padX * 2;
    return (
        <g style={{ userSelect: 'none' }}>
            <rect
                x={at.x}
                y={at.y - height / 2}
                width={width}
                height={height}
                rx={3}
                fill="rgba(0, 0, 0, 0.6)"
            />
            <text
                x={at.x + padX}
                y={at.y}
                fill={color}
                fontSize={13}
                fontFamily="sans-serif"
                fontWeight={600}
                dominantBaseline="central"
            >
                {text}
            </text>
        </g>
    );
};

/** Label position for a distance segment: nudged off the line midpoint. */
function distanceLabelPos(a: CanvasPoint, b: CanvasPoint): CanvasPoint {
    const mid = canvasMidpoint(a, b);
    const dir = unitTo(a, b);
    if (!dir) {
        return { x: mid.x + 8, y: mid.y - 8 };
    }
    // Perpendicular offset so the text doesn't sit on the line.
    return { x: mid.x - dir.y * 12 + 6, y: mid.y + dir.x * 12 - 6 };
}

/** Label position for an angle: along the interior bisector from the vertex. */
function angleLabelPos(
    vertex: CanvasPoint,
    armA: CanvasPoint,
    armB: CanvasPoint
): CanvasPoint {
    const dirA = unitTo(vertex, armA);
    const dirB = unitTo(vertex, armB);
    if (!dirA || !dirB) {
        return { x: vertex.x + 10, y: vertex.y - 10 };
    }
    let bx = dirA.x + dirB.x;
    let by = dirA.y + dirB.y;
    const length = Math.hypot(bx, by);
    if (length < 1e-6) {
        // Arms are opposite (≈180°); offset perpendicular instead.
        return { x: vertex.x - dirA.y * 18, y: vertex.y + dirA.x * 18 };
    }
    bx /= length;
    by /= length;
    const offset = ANGLE_ARC_RADIUS_PX + 12;
    return { x: vertex.x + bx * offset, y: vertex.y + by * offset };
}

interface MeasurementStyle {
    dashed?: boolean;
    opacity?: number;
    /** White glow used to emphasize the hovered/selected measurement. */
    glow?: boolean;
}

const GLOW_FILTER = 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.9))';

function renderDistance(
    points: readonly [CanvasPoint, CanvasPoint],
    label: string,
    color: string,
    keyPrefix: string,
    style: MeasurementStyle = {}
) {
    const [a, b] = points;
    const dir = unitTo(a, b);
    const labelPos = distanceLabelPos(a, b);
    return (
        <g
            key={keyPrefix}
            opacity={style.opacity ?? 1}
            style={style.glow ? { filter: GLOW_FILTER } : undefined}
        >
            <Segment a={a} b={b} color={color} dashed={style.dashed} />
            <Tick at={a} dir={dir} color={color} />
            <Tick at={b} dir={dir} color={color} />
            <ValueLabel at={labelPos} text={label} color={color} />
        </g>
    );
}

function renderAngle(
    points: readonly [CanvasPoint, CanvasPoint, CanvasPoint],
    label: string,
    color: string,
    keyPrefix: string,
    style: MeasurementStyle = {}
) {
    const [armA, vertex, armB] = points;
    const arc = angleArcPath(vertex, armA, armB);
    const labelPos = angleLabelPos(vertex, armA, armB);
    return (
        <g
            key={keyPrefix}
            opacity={style.opacity ?? 1}
            style={style.glow ? { filter: GLOW_FILTER } : undefined}
        >
            <Segment a={armA} b={vertex} color={color} dashed={style.dashed} />
            <Segment a={vertex} b={armB} color={color} dashed={style.dashed} />
            {arc && (
                <path
                    d={arc}
                    fill="none"
                    stroke={color}
                    strokeWidth={1.5}
                    strokeDasharray={style.dashed ? '5 4' : undefined}
                />
            )}
            <Tick at={armA} dir={unitTo(armA, vertex)} color={color} />
            <Dot at={vertex} color={color} />
            <Tick at={armB} dir={unitTo(armB, vertex)} color={color} />
            <ValueLabel at={labelPos} text={label} color={color} />
        </g>
    );
}

function renderCommitted(
    view: PlaneView,
    measurement: Measurement,
    onCurrentSlice: boolean,
    emphasized: boolean
) {
    // When emphasized (its row is hovered) draw it solid, full-strength, and
    // glowing — even if it sits on another slice — so it's easy to locate.
    const style: MeasurementStyle = emphasized
        ? { opacity: 1, glow: true }
        : onCurrentSlice
          ? {}
          : { dashed: true, opacity: OFF_PLANE_OPACITY };
    const label = formatMeasurementValue(measurement);
    if (measurement.kind === 'distance') {
        const [a, b] = measurement.points;
        return renderDistance(
            [patientToCanvasPoint(a, view), patientToCanvasPoint(b, view)],
            label,
            MEASUREMENT_COLOR,
            measurement.id,
            style
        );
    }
    const [armA, vertex, armB] = measurement.points;
    return renderAngle(
        [
            patientToCanvasPoint(armA, view),
            patientToCanvasPoint(vertex, view),
            patientToCanvasPoint(armB, view),
        ],
        label,
        MEASUREMENT_COLOR,
        measurement.id,
        style
    );
}

/** Render the in-progress placement: placed points + dashed rubber-band. */
function renderDraft(view: PlaneView, draft: MeasurementDraft) {
    const placed = draft.points;
    const hover = draft.hoverPointMm;

    if (draft.kind === 'distance') {
        if (placed.length === 1 && hover) {
            const a = patientToCanvasPoint(placed[0], view);
            const b = patientToCanvasPoint(hover, view);
            return renderDistance(
                [a, b],
                formatDistanceCm(euclideanDistanceMm(placed[0], hover)),
                DRAFT_COLOR,
                'draft',
                { dashed: true }
            );
        }
        // Only the first point placed, no hover yet.
        return (
            <Dot
                key="draft"
                at={patientToCanvasPoint(placed[0], view)}
                color={DRAFT_COLOR}
            />
        );
    }

    // Angle draft.
    const canvasPlaced = placed.map((p) => patientToCanvasPoint(p, view));
    if (placed.length === 1) {
        const a = canvasPlaced[0];
        return (
            <g key="draft">
                {hover && (
                    <Segment
                        a={a}
                        b={patientToCanvasPoint(hover, view)}
                        color={DRAFT_COLOR}
                        dashed
                    />
                )}
                <Dot at={a} color={DRAFT_COLOR} />
            </g>
        );
    }
    // Two points placed (armA, vertex); preview the second arm to the hover
    // point — with a live angle readout — once the cursor is in the viewport.
    const [armA, vertex] = canvasPlaced;
    const hoverCanvas = hover ? patientToCanvasPoint(hover, view) : null;
    const liveAngle = hover
        ? angleDegAtVertex(placed[0], placed[1], hover)
        : NaN;
    return (
        <g key="draft">
            <Segment a={armA} b={vertex} color={DRAFT_COLOR} />
            {hoverCanvas && (
                <Segment
                    a={vertex}
                    b={hoverCanvas}
                    color={DRAFT_COLOR}
                    dashed
                />
            )}
            {hoverCanvas && Number.isFinite(liveAngle) && (
                <ValueLabel
                    at={angleLabelPos(vertex, armA, hoverCanvas)}
                    text={formatAngleDeg(liveAngle)}
                    color={DRAFT_COLOR}
                />
            )}
            <Tick at={armA} dir={unitTo(armA, vertex)} color={DRAFT_COLOR} />
            <Dot at={vertex} color={DRAFT_COLOR} />
        </g>
    );
}

/** Half-width of the invisible band along a measurement that reveals its
 * handles on hover, in px. Wider than the drawn line so it is easy to land. */
const HANDLE_HOVER_HIT_WIDTH_PX = 14;

interface EditableHandle {
    readonly pointIndex: number;
    /** Projected canvas position of the point. */
    readonly at: CanvasPoint;
}

interface EditableSegment {
    /** Ordinal of the segment within the measurement (stable react key). */
    readonly ordinal: number;
    readonly a: CanvasPoint;
    readonly b: CanvasPoint;
}

/** One on-slice measurement, projected and ready for hover-to-edit. */
interface EditableMeasurement {
    readonly id: string;
    /** The measurement's slice coordinate, captured to keep edits coplanar. */
    readonly planeCoordinateMm: number;
    readonly handles: readonly EditableHandle[];
    readonly segments: readonly EditableSegment[];
}

interface HandleDrag {
    readonly id: string;
    readonly pointIndex: number;
    readonly planeCoordinateMm: number;
}

/**
 * Drag interaction for editing a committed measurement's point: the moved
 * point is pinned back onto the measurement's own slice, keeping all of its
 * points coplanar, and stored in the frame of the image the pane shows
 * before the record is recomputed.
 */
function useHandleDrag(
    view: PlaneView | null
): AnnotationPointDrag<HandleDrag> {
    const updatePoint = useUpdateMeasurementPoint();

    const measurementSliceCoordinateMm = useCallback(
        (drag: HandleDrag) => drag.planeCoordinateMm,
        []
    );

    const movePoint = useCallback(
        (drag: HandleDrag, pointMm: PointMm, dragView: PlaneView) => {
            const pane = paneImageOf(dragView);
            if (pane.kind === 'none') {
                return;
            }
            updatePoint(drag.id, drag.pointIndex, pane.toImageFrame(pointMm));
        },
        [updatePoint]
    );

    return useAnnotationPointDrag(
        view,
        measurementSliceCoordinateMm,
        movePoint
    );
}

/**
 * Hover-to-edit layer for one measurement: an invisible hit band along its
 * segments that reveals draggable point handles while the measurement is
 * hovered (or being dragged). The band and the handles share a single group
 * with the hover listeners, so moving from the band onto a handle never
 * counts as leaving — and `pointerEvents` opt-in stays scoped to the
 * editable geometry, leaving the rest of the overlay click-through.
 *
 * `onBandPress` forwards a left-press on the band down to the canvas (see
 * `forwardPressToCanvas`) so a new measurement can still be started on top
 * of an existing one.
 */
function renderEditable(
    editable: EditableMeasurement,
    showHandles: boolean,
    onHoverChange: (id: string | null) => void,
    onBeginDrag: (e: React.MouseEvent, drag: HandleDrag) => void,
    onBandPress: (e: React.MouseEvent) => void
) {
    const { id, handles, segments, planeCoordinateMm } = editable;
    return (
        <g
            key={`edit-${id}`}
            style={{ pointerEvents: 'all' }}
            onMouseEnter={() => onHoverChange(id)}
            onMouseLeave={() => onHoverChange(null)}
            onMouseDown={onBandPress}
        >
            {segments.map((segment) => (
                <line
                    key={`${id}-seg-${segment.ordinal}`}
                    x1={segment.a.x}
                    y1={segment.a.y}
                    x2={segment.b.x}
                    y2={segment.b.y}
                    stroke="transparent"
                    strokeWidth={HANDLE_HOVER_HIT_WIDTH_PX}
                    strokeLinecap="round"
                />
            ))}
            {showHandles &&
                handles.map((handle) => (
                    <circle
                        key={`${id}-handle-${handle.pointIndex}`}
                        data-testid="measurement-handle"
                        cx={handle.at.x}
                        cy={handle.at.y}
                        r={HANDLE_RADIUS_PX}
                        fill="rgba(0, 0, 0, 0.35)"
                        stroke="#fff"
                        strokeWidth={1.5}
                        style={{ cursor: 'move' }}
                        onMouseDown={(e) =>
                            onBeginDrag(e, {
                                id,
                                pointIndex: handle.pointIndex,
                                planeCoordinateMm,
                            })
                        }
                    />
                ))}
        </g>
    );
}

/**
 * Renders distance and angle measurements (and the live placement preview)
 * as an SVG overlay above the image. Display-only except for the per-
 * measurement hover-to-edit layer: the svg sets `pointerEvents: none` and
 * only the invisible hover band (and the handles it reveals) opt back in, so
 * it otherwise never intercepts the engine tool's mouse events. It
 * re-renders on pan / zoom / slice / registration change by subscribing to
 * the view manager's events.
 *
 * The measurements of the series the pane shows are drawn, primary or fused
 * secondary, each moved from its image's own frame onto the pane through the
 * registration read now.
 */
const MeasurementOverlay = ({ view }: MeasurementOverlayProps) => {
    const measurements = useRecoilValue(measurementListState.items);
    const draft = useRecoilValue(measurementDraftState);
    const highlightedId = useRecoilValue(highlightedMeasurementState);
    const selectedTool = useRecoilValue(selectedToolState);
    const viewManager = useRecoilValue(viewManagerState);
    const { beginDrag, dragging } = useHandleDrag(view);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const renderTick = usePlaneRedrawTick(viewManager);

    /**
     * Forward a left-press on a measurement's hover band down to the view
     * canvas. The band opts into pointer events (for hover-to-edit), so a
     * press targets it rather than the canvas, yet the engine only starts a
     * placement on a press whose target is the canvas (ViewManager). Re-
     * dispatching the press on the canvas routes it through the exact same
     * engine path a direct canvas click takes (modifier keys, pan-meta sync,
     * tool dispatch); passing the native event as the init copies its button,
     * coordinates, modifier keys, and bubbling through unchanged. Only the
     * left button (the one that places points) is forwarded, and the original
     * press's propagation is stopped so the engine sees it once, as a single
     * canvas click would.
     * Handle presses never reach here — their onMouseDown stops propagation —
     * so dragging an existing point is unchanged.
     */
    const forwardPressToCanvas = useCallback(
        (e: React.MouseEvent) => {
            const canvas = view?.canvas;
            if (!canvas || e.button !== MOUSE_BUTTON_LEFT) {
                return;
            }
            e.stopPropagation();
            canvas.dispatchEvent(new MouseEvent('mousedown', e.nativeEvent));
        },
        [view]
    );

    const content = useMemo(() => {
        if (!view) {
            return null;
        }
        const pane = paneImageOf(view);
        if (pane.kind === 'none') {
            return null;
        }
        const ontoPane = imageToPaneFrame(pane);
        const tolerance = displayedSliceToleranceMm(view);
        // Show every measurement of this view's image + orientation on all of
        // its slices; those placed on a different slice render dimmed +
        // dashed. Measurements from another image never match, so they are
        // not drawn here at all. On-slice status is resolved once per
        // measurement here and reused for both styling and the edit gate.
        const visibleMeasurements = measurements
            .filter((m) => m.visible && viewShowsPlane(view, m.plane))
            .map((stored) => {
                const m = measurementInFrame(stored, ontoPane);
                return {
                    measurement: m,
                    onCurrentSlice: arePointsOnDisplayedSlice(
                        m.points,
                        view,
                        m.plane,
                        tolerance
                    ),
                };
            });
        const draftOnView =
            draft && viewShowsPlane(view, draft.plane)
                ? measurementDraftInFrame(draft, ontoPane)
                : null;

        if (visibleMeasurements.length === 0 && !draftOnView) {
            return null;
        }

        // Editing is offered only while the tool is active and no placement
        // is in progress (a draft click must reach the canvas, not a hover
        // band), and only for measurements lying on the slice actually shown.
        // Each one's handles surface on hover and stay up while it is dragged.
        const editingEnabled = selectedTool === 'measurement' && !draft;
        const editables: EditableMeasurement[] = [];
        if (editingEnabled) {
            for (const {
                measurement: m,
                onCurrentSlice,
            } of visibleMeasurements) {
                if (!onCurrentSlice) {
                    continue;
                }
                const projected = m.points.map((point) =>
                    patientToCanvasPoint(point, view)
                );
                const segments: EditableSegment[] = [];
                for (let i = 0; i < projected.length - 1; i += 1) {
                    segments.push({
                        ordinal: i,
                        a: projected[i],
                        b: projected[i + 1],
                    });
                }
                editables.push({
                    id: m.id,
                    planeCoordinateMm: m.plane.planeCoordinateMm,
                    handles: projected.map((at, index) => ({
                        pointIndex: index,
                        at,
                    })),
                    segments,
                });
            }
        }

        return (
            <>
                {visibleMeasurements.map(({ measurement: m, onCurrentSlice }) =>
                    renderCommitted(
                        view,
                        m,
                        onCurrentSlice,
                        m.id === highlightedId
                    )
                )}
                {draftOnView && renderDraft(view, draftOnView)}
                {editables.map((editable) =>
                    renderEditable(
                        editable,
                        editable.id === hoveredId ||
                            editable.id === dragging?.id,
                        setHoveredId,
                        beginDrag,
                        forwardPressToCanvas
                    )
                )}
            </>
        );
        // renderTick (via forceUpdate) refreshes projections on view change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        view,
        measurements,
        draft,
        highlightedId,
        renderTick,
        selectedTool,
        beginDrag,
        dragging,
        hoveredId,
        forwardPressToCanvas,
    ]);

    if (!content) {
        return null;
    }

    return <AnnotationOverlaySvg>{content}</AnnotationOverlaySvg>;
};

export default MeasurementOverlay;
